<template>
  <div
    id="home"
    ref="resizeElement"
    class="text"
    :class="{ light: !darkMode, hasScrollBar: hasScrollBar }"
  >
    <div id="citation">{{ $t("home.citation") }}</div>
    <div id="profil">{{ $t("home.profil") }}</div>
    <div
      class="stack-container"
      v-for="(stackList, index) in stacks"
      :key="index"
    >
      <div class="title" :class="{ light: !darkMode }">
        {{ $t("home." + stackList.name) }}
      </div>
      <div class="stack-list">
        <div
          class="stack"
          :class="{ light: !darkMode }"
          v-for="(stack, index) in stackList.stackList"
          :key="index"
        >
          {{ getStackDisplayName(stack) }}
        </div>
      </div>
    </div>
    <div
      id="resume"
      :class="{ light: !darkMode }"
      @click="openPDF"
      aria-label="Download Resume"
    >
      ↓ {{ $t("home.resume") }}
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";
import store from "@/store";
import axios from "axios";

interface StackGroup {
  name: string;
  stackList: Array<string>;
}

interface shortNameKey {
  name: string;
  shortName: string;
}

export default defineComponent({
  name: "Home",
  data(): {
    isSmallScreen: boolean;
    stacks: Array<StackGroup>;
    hasScrollBar: boolean;
    observer: ResizeObserver | null;
    shortStack: Array<shortNameKey>;
  } {
    return {
      isSmallScreen: window.innerWidth < 400,
      stacks: [
        {
          name: "lang",
          stackList: [
            "JavaScript",
            "TypeScript",
            "PHP",
            "SQL",
            "HTML",
            "CSS",
            "Python",
            "C",
            "C#",
          ],
        },
        {
          name: "framework",
          stackList: ["NodeJS", "Laravel", "VueJS"],
        },
        {
          name: "lib",
          stackList: ["jQuery", "Tailwind", "Vuetify"],
        },
      ],
      hasScrollBar: false,
      observer: null, // Stocker l'observer
      shortStack: [
        {
          name: "JavaScript",
          shortName: "JS",
        },
        {
          name: "TypeScript",
          shortName: "TS",
        },
      ],
    };
  },
  computed: {
    darkMode() {
      return store.state.darkMode;
    },
  },
  methods: {
    async openPDF() {
      let pdfPath = "/assets/CV_Armand_CHAVY_FR.pdf";
      if (this.$i18n.locale === "en") {
        pdfPath = "/assets/CV_Armand_CHAVY_EN.pdf";
      }

      const response = await axios.get(pdfPath, {
        responseType: "arraybuffer",
      });
      let pdfContent = new Uint8Array(response.data);

      const blob = new Blob([pdfContent], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");

      // Révoque l'URL de données après l'ouverture
      window.URL.revokeObjectURL(url);
    },
    getStackDisplayName(stack: string): string {
      if (this.isSmallScreen) {
        const short = this.shortStack.find((item) => item.name === stack);
        return short ? short.shortName : stack;
      }
      return stack;
    },
    handleResize() {
      const el = this.$refs.resizeElement as HTMLElement;
      this.hasScrollBar = el.scrollHeight > el.clientHeight;
      this.isSmallScreen = window.innerWidth < 400;
    },
  },
  mounted() {
    const element = this.$refs.resizeElement as HTMLElement;
    if (element) {
      this.observer = new ResizeObserver(this.handleResize);
      this.observer.observe(element);
    }
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect(); // Nettoyer l'observer
    }
  },
});
</script>

<style scoped>
#home {
  position: absolute;
  z-index: 100;
  right: 20px;
  bottom: 20px;
  max-width: calc(100% - 62px);
  max-height: calc(100% - 242px);
  overflow-y: auto;
  border: solid 1px #ececec;
  background: rgba(33, 33, 33, 0.8);
  backdrop-filter: blur(2px);
  box-shadow: 0 4px 15px rgba(33, 33, 33, 0.2);
  border-radius: 5px;
  overflow: auto;
  padding: 10px 10px 10px 10px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

#home.hasScrollBar {
  padding-right: 2px;
  max-width: calc(100% - 54px);
}

#home.light {
  border-color: #212121;
  background: rgba(236, 236, 236, 0.8);
  box-shadow: 0 4px 15px rgba(236, 236, 236, 0.2);
}

#citation {
  align-self: center;
  text-align: center;
  font-size: 14px;
  margin-bottom: 10px;
}

.title {
  border-bottom: 1px solid;
  border-color: #ececec;
  margin-bottom: 6px;
  margin: 10px 0px;
  text-align: center;
  font-size: 14px;
  padding-bottom: 5px;
}

.title.light {
  border-color: #212121;
}

.stack-list {
  display: flex;
  flex-wrap: wrap; /* Permet de passer à la ligne */
  gap: 10px; /* Espacement entre les items */
  list-style: none; /* Retire les puces */
  padding: 0;
  margin: 0;
}

.stack {
  flex: 1 1 calc(33.33% - 10px); /* Prend 1/3 de la largeur moins l'espace */
  box-sizing: border-box; /* Inclut les marges et bordures dans la taille */
  padding: 10px 0px;
  text-align: center;
  border: solid 1px #ececec;
  border-radius: 5px;
}

.stack.light {
  border-color: #212121;
}

#resume {
  padding: 20px 0 10px 0;
  transition: opacity 0.3s ease;
  text-align: center;
  cursor: pointer;
}

@media (hover: hover) and (pointer: fine) {
  #resume:hover {
    opacity: 0.25;
  }

  #resume:active {
    opacity: 1;
  }
}

@media (orientation: landscape) {
  #home {
    max-width: calc(100% - 321px);
    max-height: calc(100% - 62px);
  }

  #home.hasScrollBar {
    max-width: calc(100% - 329px);
  }
}

@media (min-width: 768px) {
  #home {
    right: 30px;
    bottom: 30px;
    border-radius: 10px;
    padding: 20px;
    max-width: calc(100% - 101px);
    max-height: calc(100% - 353px);
  }

  #home.hasScrollBar {
    max-width: calc(100% - 93px);
    padding-right: 12px;
  }

  #citation {
    font-size: 20px;
    max-width: 450px;
  }

  .title {
    font-size: 20px;
    padding-bottom: 10px;
    margin: 20px 0;
  }

  .stack-list {
    gap: 20px;
  }

  .stack {
    flex: 1 1 calc(33.33% - 20px);
  }

  #resume {
    padding-top: 30px;
  }
}

@media screen AND (min-width: 768px) AND (orientation: landscape) {
  #home {
    max-width: calc(100% - 402px);
    max-height: calc(100% - 102px);
  }

  #home.hasScrollBar {
    max-width: calc(100% - 390px);
  }
}

@media screen and (min-width: 1200px) {
  #home {
    max-width: 600px;
  }
  #home.hasScrollBar {
    max-width: 600px;
  }

  #citation {
    font-size: 26px;
  }

  .title {
    font-size: 26px;
  }
}
</style>
